import React, { useState, useEffect } from "react";
import api from "../../utils/api/axiosInstance";
import { capMatchPercent } from "../../utils/matchScoreUtils";
import { useUserAuth } from "../../contexts/UserAuthContext";
import { getMatchExplanationBody } from "../../utils/fetchProductMatchExplanation";
import { hasMatchIntent } from "../../utils/resolveMatchIntent";
import MatchFusedScoreLine from "./MatchFusedScoreLine";
import { getDimensionScoreTier } from "../../pages/product/helpers/productDetailViewHelpers";
import styles from "../../styles/components/product/MatchExplanation.module.css";
import {
  FaCheckCircle,
  FaTimesCircle,
  FaInfoCircle,
  FaChartLine,
  FaUsers,
  FaLightbulb,
  FaDollarSign,
  FaStar,
  FaArrowUp,
  FaThumbsUp,
} from "react-icons/fa";

const DIMENSION_BAR_CLASS = {
  success: styles.barSuccess,
  warning: styles.barWarning,
  error: styles.barError,
};

const DIMENSION_SCORE_CLASS = {
  success: styles.scoreSuccess,
  warning: styles.scoreWarning,
  error: styles.scoreError,
};

const normalizeKey = (value) => {
  if (value && typeof value === "object") {
    return String(value.label ?? value.name ?? value.value ?? "")
      .toLowerCase()
      .trim();
  }
  return String(value || "").toLowerCase().trim();
};

const matchesMustHave = (spec, mustHave) => {
  const specText = `${spec.label || spec.key} ${spec.value}`.toLowerCase();
  const need = normalizeKey(mustHave);
  return need && specText.includes(need);
};

/** Must-have / nice-to-have spec matches — folded into Key Highlights so
 * "what matters for you" isn't a separate, redundant card on the page. */
const buildSpecMatchHighlights = (specs, wizardData) => {
  const mustHaves = wizardData?.mustHaves || [];
  const niceToHaves = wizardData?.niceToHaves || [];
  if (!specs?.length || (!mustHaves.length && !niceToHaves.length)) return [];

  const items = [];
  for (const spec of specs) {
    for (const mh of mustHaves) {
      if (matchesMustHave(spec, mh)) {
        items.push({
          key: `mh-${spec.key || spec.label}`,
          label: `${spec.label || spec.key}: ${spec.value} — matches your must-have`,
        });
      }
    }
    for (const nth of niceToHaves) {
      if (matchesMustHave(spec, nth)) {
        items.push({
          key: `nth-${spec.key || spec.label}`,
          label: `${spec.label || spec.key}: ${spec.value} — aligns with your nice-to-have`,
        });
      }
    }
  }

  const seen = new Set();
  return items.filter((item) => {
    if (seen.has(item.key)) return false;
    seen.add(item.key);
    return true;
  }).slice(0, 4);
};

const DIMENSION_LABELS = {
  feature: "Feature Match",
  value: "Value Score",
  budget: "Budget Fit",
  review: "Review Quality",
  specCoverage: "Spec Coverage",
  availability: "Spec Coverage",
  trend: "Popularity",
};

/**
 * MatchExplanation Component (Phase 5 Enhanced)
 * Displays detailed explanation of why a product matches user's needs
 * Now includes social proof and budget intelligence insights
 * @param {Object} product - Product object
 * @param {Object} wizardData - AI chat match answers from user
 * @param {Object} matchData - Pre-fetched match from useProductInsights (avoids duplicate API call)
 * @param {boolean} matchLoading - Loading state when matchData comes from parent
 * @param {Function} onClose - Optional close handler
 * @param {String} theme - Theme variant: 'light' (default) or 'dark'
 */
const MatchExplanation = ({
  product,
  wizardData,
  specs = [],
  matchData: prefetchedMatchData,
  matchLoading: prefetchedMatchLoading = false,
  usePrefetchedMatch = false,
  heroSummaryShown = false,
  onClose,
  theme = "light",
  children,
}) => {
  const { user } = useUserAuth();
  const [localMatchData, setLocalMatchData] = useState(null);
  const [budgetInsights, setBudgetInsights] = useState(null);
  const [clusterSocialProof, setClusterSocialProof] = useState(null);
  const [localLoading, setLocalLoading] = useState(true);
  const [error, setError] = useState(null);

  // When the parent already owns match fetching (useProductInsights), never
  // fall back to an independent fetch here - even a `null` prefetched value
  // (no match available) must be honored so this section can't disagree
  // with the hero verdict shown elsewhere on the page.
  const matchData = usePrefetchedMatch
    ? prefetchedMatchData
    : prefetchedMatchData ?? localMatchData;
  const loading = usePrefetchedMatch
    ? prefetchedMatchLoading
    : prefetchedMatchData
      ? prefetchedMatchLoading
      : localLoading;

  useEffect(() => {
    if (usePrefetchedMatch) return;
    if (!product?._id) return;
    if (!user && !wizardData) return;
    if (prefetchedMatchData) return;

    fetchMatchData();
  }, [product?._id, wizardData, user, prefetchedMatchData, usePrefetchedMatch]);

  useEffect(() => {
    if (!product?._id) return;
    const intentAnswers =
      wizardData && hasMatchIntent(wizardData)
        ? wizardData
        : getMatchExplanationBody(user);
    if (!user && !hasMatchIntent(intentAnswers)) return;

    fetchSupplementaryData(intentAnswers);
  }, [product?._id, wizardData, user]);

  const fetchMatchData = async () => {
    if (!product?._id) {
      setError("Product ID is missing");
      return;
    }

    try {
      setLocalLoading(true);
      setError(null);

      const matchBody = getMatchExplanationBody(user);
      const matchRes = await api.post(
        `/products/${product._id}/match-explanation`,
        matchBody,
      );

      if (matchRes.data?.match) {
        setLocalMatchData(matchRes.data.match);
      } else {
        setError("Invalid response from server");
      }
    } catch (err) {
      console.error("Error fetching match data:", err);
      setError("Failed to load match explanation");
    } finally {
      setLocalLoading(false);
    }
  };

  const fetchSupplementaryData = async (intentAnswers) => {
    if (!product?._id) return;

    try {
      const wizardAnswers =
        intentAnswers && hasMatchIntent(intentAnswers)
          ? intentAnswers
          : getMatchExplanationBody(user);

      const requests = [
        api
          .post(`/budget-intelligence/product/${product._id}`, {
            wizardAnswers,
          })
          .catch(() => null),
      ];

      if (user?.id || user?._id) {
        requests.push(
          api.get(`/products/${product._id}/social-proof`).catch(() => null),
        );
      }

      const results = await Promise.all(requests);
      const budgetRes = results[0];
      const socialRes = user ? results[1] : null;

      if (budgetRes?.data && !budgetRes.data.error) {
        setBudgetInsights(budgetRes.data);
      }

      if (socialRes?.data?.hasSocialProof) {
        setClusterSocialProof(socialRes.data);
      } else {
        setClusterSocialProof(null);
      }
    } catch (err) {
      console.error("Error fetching supplementary match data:", err);
    }
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading match analysis...</div>
      </div>
    );
  }

  if (error || !matchData) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>
          {error || "Unable to generate match explanation"}
        </div>
      </div>
    );
  }

  const { overallScore, dimensions, explanation, dimensionsList, dealBreakerFailed } =
    matchData;

  const displayDimensions =
    Array.isArray(dimensionsList) && dimensionsList.length > 0
      ? dimensionsList
      : Object.entries(dimensions || {})
          .filter(
            ([key, score]) =>
              score != null && key !== "availability" && key !== "omittedDimensions",
          )
          .map(([key, score]) => ({
            key,
            name: DIMENSION_LABELS[key] || key,
            score: Math.round(Math.min(100, Math.max(0, score))),
            reason: null,
          }));

  const dimensionScoreMap = displayDimensions.reduce((acc, dim) => {
    acc[dim.key] = dim.score;
    return acc;
  }, {});

  const specHighlights = buildSpecMatchHighlights(specs, wizardData);

  const budgetMax = Number(wizardData?.budget?.max);
  const hasBudgetMax = Number.isFinite(budgetMax);
  const productPrice = Number(product?.price);
  const hasProductPrice = Number.isFinite(productPrice);
  const isOverBudgetByPrice = hasBudgetMax && hasProductPrice && productPrice > budgetMax;
  const isWithinBudgetByPrice = hasBudgetMax && hasProductPrice && productPrice <= budgetMax;

  const positiveBudgetClaimPattern =
    /\b(within|under)\s+your budget\b|fits(?: comfortably)?\s+within\s+your budget|perfectly within your budget/i;
  const hasPositiveBudgetClaim =
    typeof explanation === "string" && positiveBudgetClaimPattern.test(explanation);

  // The price-vs-budget fact is a hard number and always wins. If the
  // server's narrative text claims the product is within budget but the
  // actual price/budget comparison disagrees (e.g. the narrative was
  // generated from a different budget snapshot), drop the false claim
  // instead of appending a second sentence that contradicts it.
  const overage = hasBudgetMax && hasProductPrice ? productPrice - budgetMax : null;
  const explanationText =
    isOverBudgetByPrice && hasPositiveBudgetClaim
      ? [
          explanation
            .split(/(?<=[.!?])\s+/)
            .filter((sentence) => !positiveBudgetClaimPattern.test(sentence))
            .join(" ")
            .trim(),
          `This is $${Math.round(overage).toLocaleString()} over your $${budgetMax.toLocaleString()} budget.`,
        ]
          .filter(Boolean)
          .join(" ")
      : explanation;

  // Determine match quality
  const getMatchQuality = (score) => {
    if (score >= 80)
      return {
        label: "Excellent match",
        icon: FaCheckCircle,
        variant: "excellent",
      };
    if (score >= 60)
      return { label: "Good match", icon: FaCheckCircle, variant: "good" };
    if (score >= 55)
      return { label: "Fair match", icon: FaInfoCircle, variant: "fair" };
    return { label: "Weak match", icon: FaTimesCircle, variant: "weak" };
  };

  const matchQuality = getMatchQuality(overallScore);
  const QualityIcon = matchQuality.icon;

  const isDarkTheme = theme === "dark";
  const showScoreSummary = !heroSummaryShown;
  const showKeyHighlights = !heroSummaryShown;
  const showSocialProof = Boolean(budgetInsights?.socialProof?.hasSocialData);
  const showValueAnalysis = Boolean(budgetInsights?.valueAnalysis?.hasData);
  const hasChildContent = React.Children.count(children) > 0;
  const hasVisibleContent =
    showScoreSummary ||
    showKeyHighlights ||
    showSocialProof ||
    showValueAnalysis ||
    hasChildContent;

  if (heroSummaryShown && !hasVisibleContent) {
    return null;
  }

  return (
    <div className={`${styles.container} ${isDarkTheme ? styles.darkTheme : ""}`}>
      {showScoreSummary && (
        <div className={styles.header}>
          <h3 className={styles.title}>
            <FaChartLine /> Why This Fits You
          </h3>
          {onClose && (
            <button className={styles.closeButton} onClick={onClose}>
              ×
            </button>
          )}
        </div>
      )}

      {showScoreSummary && (
        <div
          className={`${styles.scoreCard} ${
            matchQuality.variant === "excellent"
              ? styles.qualityExcellent
              : matchQuality.variant === "good"
                ? styles.qualityGood
                : matchQuality.variant === "fair"
                  ? styles.qualityFair
                  : styles.qualityWeak
          }`}
        >
          <div className={styles.scoreCircle}>
            <span className={styles.scoreValue}>{capMatchPercent(overallScore)}%</span>
            <span className={styles.scoreLabel}>Match</span>
          </div>
          <div className={styles.scoreInfo}>
            <div className={styles.qualityBadge}>
              <QualityIcon />
              <span>{matchQuality.label}</span>
            </div>
            <MatchFusedScoreLine
              score={overallScore}
              proof={clusterSocialProof}
              className={styles.fusedMatchLine}
            />
            <p className={styles.explanationText}>{explanationText}</p>
            {dealBreakerFailed && (
              <p className={styles.dealBreakerWarning}>
                This product is missing a must-have feature from your profile.
              </p>
            )}
          </div>
        </div>
      )}

      {showScoreSummary && (
        <div className={styles.dimensionsSection}>
          <h4 className={styles.sectionTitle}>Detailed Breakdown</h4>
          <div className={styles.dimensionsGrid}>
            {displayDimensions.map((dimension) => {
              const cappedScore = Math.min(100, Math.max(0, dimension.score));
              const tier = getDimensionScoreTier(cappedScore);

              return (
                <div key={dimension.key} className={styles.dimensionCard}>
                  <div className={styles.dimensionHeader}>
                    <span className={styles.dimensionName}>
                      {dimension.name}
                    </span>
                    <span
                      className={`${styles.dimensionScore} ${DIMENSION_SCORE_CLASS[tier]}`}
                    >
                      {Math.round(cappedScore)}%
                    </span>
                  </div>
                  <div className={styles.scoreBar}>
                    <div
                      className={`${styles.scoreBarFill} ${DIMENSION_BAR_CLASS[tier]}`}
                      style={{ "--score-width": `${cappedScore}%` }}
                    />
                  </div>
                  {dimension.reason && (
                    <p className={styles.dimensionReason}>{dimension.reason}</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {showKeyHighlights && (
      <div className={styles.highlightsSection}>
        <h4 className={styles.sectionTitle}>Key Highlights</h4>
        <ul className={styles.highlightsList}>
          {overallScore >= 85 && (
            <li className={styles.highlightItem}>
              <FaCheckCircle className={styles.highlightIconPositive} />
              <span>Strong alignment with your priorities</span>
            </li>
          )}
          {dimensionScoreMap.feature >= 80 && (
            <li className={styles.highlightItem}>
              <FaCheckCircle className={styles.highlightIconPositive} />
              <span>Meets most of your specified features</span>
            </li>
          )}
          {dimensionScoreMap.value >= 75 && (
            <li className={styles.highlightItem}>
              <FaCheckCircle className={styles.highlightIconPositive} />
              <span>Excellent value for the price</span>
            </li>
          )}
          {isWithinBudgetByPrice && (
            <li className={styles.highlightItem}>
              <FaCheckCircle className={styles.highlightIconPositive} />
              <span>Fits comfortably within your budget</span>
            </li>
          )}
          {isOverBudgetByPrice && (
            <li className={`${styles.highlightItem} ${styles.highlightWarning}`}>
              <FaInfoCircle />
              <span>Above budget, but offers exceptional value</span>
            </li>
          )}
          {dimensionScoreMap.review >= 80 && (
            <li className={styles.highlightItem}>
              <FaCheckCircle className={styles.highlightIconPositive} />
              <span>Highly rated by other users</span>
            </li>
          )}
          {specHighlights.map((item) => (
            <li key={item.key} className={styles.highlightItem}>
              <FaCheckCircle className={styles.highlightIconPositive} />
              <span>{item.label}</span>
            </li>
          ))}
        </ul>
      </div>
      )}

      {children}

      {/* Social Proof Section */}
      {showSocialProof && (
        <div className={styles.socialProofSection}>
          <h4 className={styles.sectionTitle}>
            <FaUsers /> What Similar Users Say
          </h4>
          <div className={styles.socialProofGrid}>
            {budgetInsights.socialProof.avgSatisfaction && (
              <div className={styles.socialProofCard}>
                <div className={styles.socialProofIcon}>
                  <FaStar />
                </div>
                <div className={styles.socialProofContent}>
                  <span className={styles.socialProofValue}>
                    {budgetInsights.socialProof.avgSatisfaction}/5
                  </span>
                  <span className={styles.socialProofLabel}>
                    Satisfaction from {budgetInsights.socialProof.similarBudgetCount} users with similar budget
                  </span>
                </div>
              </div>
            )}
            {budgetInsights.socialProof.stretchedCount > 0 && (
              <div className={styles.socialProofCard}>
                <div className={styles.socialProofIcon}>
                  <FaArrowUp />
                </div>
                <div className={styles.socialProofContent}>
                  <span className={styles.socialProofValue}>
                    {budgetInsights.socialProof.percentStretch}%
                  </span>
                  <span className={styles.socialProofLabel}>
                    of buyers stretched their budget
                    {budgetInsights.socialProof.stretchedAvgSatisfaction && 
                      ` (${budgetInsights.socialProof.stretchedAvgSatisfaction}/5 satisfied)`}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Budget analysis lives in ProductDeeperInsightsSection → BudgetAnalysis */}
      {showValueAnalysis && (
        <div className={styles.valueAnalysisSection}>
          <h4 className={styles.sectionTitle}>
            <FaChartLine /> Value Perception
          </h4>
          <div className={styles.valueAnalysisContent}>
            {budgetInsights.valueAnalysis.avgValueRating && (
              <div className={styles.valueMetric}>
                <span className={styles.valueMetricLabel}>Value Rating</span>
                <span className={styles.valueMetricValue}>
                  {budgetInsights.valueAnalysis.avgValueRating}/5
                </span>
              </div>
            )}
            <div className={`${styles.valueSentiment} ${
              budgetInsights.valueAnalysis.valueSentiment === "positive" ? styles.sentimentPositive :
              budgetInsights.valueAnalysis.valueSentiment === "negative" ? styles.sentimentNegative :
              styles.sentimentNeutral
            }`}>
              {budgetInsights.valueAnalysis.valueSentiment === "positive" && (
                <span>Users frequently mention this product offers great value</span>
              )}
              {budgetInsights.valueAnalysis.valueSentiment === "negative" && (
                <span>Some users feel this product is pricey</span>
              )}
              {budgetInsights.valueAnalysis.valueSentiment === "neutral" && (
                <span>Value perception is mixed among users</span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MatchExplanation;
